import dayjs from 'dayjs'
import { useGeckoAnalytics } from 'features/analytics'
import { DataLayer, TimeRange } from 'features/systemLens'
import { TimeSeriesService, useScatterplotContext } from 'features/timeSeries'
import { PlotHoverEvent, PlotRelayoutEvent } from 'plotly.js'
import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import {
  EnhancedSensorEntry,
  LocalThresholdOverrideType,
  SensorThresholdConfig,
} from 'types'

interface UseChartInteractionFunctionsProps {
  clearCursorData: () => void
  setTimeRange: React.Dispatch<React.SetStateAction<TimeRange>>
  allSensorsToRender: Array<{ id: string; name: string }>
  enhancedSensorEntries: EnhancedSensorEntry[]
  updateCursorData: (
    sensorId: string,
    value: number,
    timestamp: number,
    unit: string
  ) => void
  setShowThresholdModal: Dispatch<SetStateAction<boolean>>
  selectedSensors: Array<{ id: string }>
  unitID?: string
  unitName?: string
  siteID: string
  setLocalThresholdOverrides: Dispatch<
    SetStateAction<LocalThresholdOverrideType>
  >
  setDataLayers: Dispatch<SetStateAction<DataLayer[]>>
}

export const useChartInteractionFunctions = ({
  clearCursorData,
  setTimeRange,
  allSensorsToRender,
  enhancedSensorEntries,
  updateCursorData,
  setShowThresholdModal,
  selectedSensors,
  unitID,
  unitName,
  siteID,
  setLocalThresholdOverrides,
  setDataLayers,
}: UseChartInteractionFunctionsProps) => {
  const { unitMapping } = useScatterplotContext()
  const { trackButtonClicked } = useGeckoAnalytics()
  const [cursorPosition, setCursorPosition] = useState<{
    x: number
    y: number
    clientX: number
    clientY: number
  } | null>(null)

  const handleUnhover = useCallback(() => {
    clearCursorData()
    setCursorPosition(null)
  }, [clearCursorData, setCursorPosition])

  const handleRelayout = useCallback(
    (event: PlotRelayoutEvent) => {
      // Check if this is a zoom event (has xaxis.range)
      if (!event['xaxis.range[0]'] || !event['xaxis.range[1]']) {
        return
      }

      // Check for autorange (zoom reset)
      if (event['xaxis.autorange'] === true) {
        return
      }

      const newFromDate = event['xaxis.range[0]']
      const newToDate = event['xaxis.range[1]']

      const fromISO = dayjs(newFromDate).toISOString()
      const toISO = dayjs(newToDate).toISOString()

      // Calculate new interval based on the zoomed time range
      const timeRangeSeconds =
        (new Date(toISO).getTime() - new Date(fromISO).getTime()) / 1000
      const { interval, interval_unit } =
        TimeSeriesService.getInterval(timeRangeSeconds)

      const newTimeRange = {
        from: fromISO,
        to: toISO,
        interval,
        intervalUnit: interval_unit,
      }
      setTimeRange(newTimeRange)
    },
    [setTimeRange]
  )

  const handleHover = useCallback(
    (event: PlotHoverEvent) => {
      if (event.points && event.points.length > 0) {
        event.points.forEach((point) => {
          const traceName = point.data.name
          if (traceName && typeof point.y === 'number') {
            const sensor = allSensorsToRender.find((sensor) => {
              const sensorEntry = enhancedSensorEntries.find(
                (e) => e.id === sensor.id
              )
              if (!sensorEntry) return false
              const displayName = sensorEntry.data?.name || sensor.id
              return displayName === traceName
            })

            if (
              sensor &&
              (typeof point.x === 'number' || typeof point.x === 'string')
            ) {
              const unit = unitMapping[traceName] || 'No Unit'
              updateCursorData(sensor.id, point.y, Number(point.x), unit)
            }
          }
        })
      }
    },
    [updateCursorData, unitMapping, allSensorsToRender, enhancedSensorEntries]
  )

  const handleConfigureThresholds = useCallback(() => {
    setShowThresholdModal(true)
    trackButtonClicked({
      button_name: 'Configure Thresholds',
      sensors_selected_count: selectedSensors.length,
      unit_id: unitID || '',
      unit_name: unitName,
      site_id: siteID,
      page: 'Asset Lens',
      tab: 'Analysis',
    })
  }, [
    setShowThresholdModal,
    trackButtonClicked,
    selectedSensors.length,
    unitID,
    unitName,
    siteID,
  ])

  const handleSaveThresholdConfigurations = useCallback(
    (thresholdConfigs: SensorThresholdConfig[]) => {
      const overrides: LocalThresholdOverrideType = {}
      thresholdConfigs.forEach((config) => {
        if (config.enabled) {
          overrides[config.sensorId] = {
            sensorId: config.sensorId,
            enabled: true,
            lowerLowerThreshold: config.lowerLowerThreshold || null,
            lowerThreshold: config.lowerThreshold || null,
            upperThreshold: config.upperThreshold || null,
            upperUpperThreshold: config.upperUpperThreshold || null,
          }
        } else {
          overrides[config.sensorId] = {
            enabled: false,
            sensorId: config.sensorId,
            lowerLowerThreshold: null,
            lowerThreshold: null,
            upperThreshold: null,
            upperUpperThreshold: null,
          }
        }
      })

      setLocalThresholdOverrides((prev) => ({ ...prev, ...overrides }))
    },
    [setLocalThresholdOverrides]
  )

  const toggleLayer = useCallback(
    (layerId: string) => {
      setDataLayers((prev) =>
        prev.map((layer) =>
          layer.id === layerId ? { ...layer, enabled: !layer.enabled } : layer
        )
      )
    },
    [setDataLayers]
  )

  return {
    handleUnhover,
    handleRelayout,
    handleHover,
    handleConfigureThresholds,
    handleSaveThresholdConfigurations,
    toggleLayer,
    cursorPosition,
    setCursorPosition,
  }
}
